import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Input } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import {
  LeaderboardDto,
  LeaderboardUpsertDto,
  PartnerDto,
} from '@/common/models/gamification'
import {
  createLeaderboard,
  deleteLeaderboard,
  getLeaderboards,
  getPartners,
  regenerateLeaderboard,
  setLeaderboardActive,
  setLeaderboardOrdinal,
  updateLeaderboard,
} from '@/core/services/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { CreateLeaderboardModal } from './CreateLeaderboardModal'
import { RebuildConfirmationModal } from './RebuildConfirmationModal'
import { StandingsModal } from './StandingsModal'
import { translationName } from './shared/labels'

/** Derived label used when a board has no localized name (or none matching). */
const fallbackLabel = (board: LeaderboardDto) => {
  const scopeLabel = board.scope === 'FESTIVAL' ? 'Festival' : 'Partner'
  const kindLabel = board.kind === 'DEFAULT' ? board.periodType ?? 'ALL_TIME' : 'custom'
  return `${scopeLabel} · ${kindLabel}`
}

export const LeaderboardsPage: React.FC = () => {
  const { editionId } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [boards, setBoards] = useState<LeaderboardDto[]>([])
  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<LeaderboardDto | null>(null)

  // In-flight ordinal edits, keyed by board id; cleared when the edit is committed or reverted.
  const [ordinalDrafts, setOrdinalDrafts] = useState<Record<string, string>>({})

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<LeaderboardDto | null>(null)

  const [standingsOpen, setStandingsOpen] = useState(false)
  const [standingsBoardId, setStandingsBoardId] = useState<string | null>(null)

  const [rebuildOpen, setRebuildOpen] = useState(false)
  const [rebuildTarget, setRebuildTarget] = useState<LeaderboardDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const [{ data: boardsData }, { data: partnersData }] = await Promise.all([
        getLeaderboards(editionId),
        getPartners(editionId),
      ])
      setBoards(boardsData ?? [])
      setPartners(partnersData ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId])

  useEffect(() => {
    getData()
  }, [getData])

  const partnerName = useCallback(
    (partnerId?: string | null) =>
      partners.find((p) => p.id === partnerId)?.name ?? '—',
    [partners],
  )

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: LeaderboardDto) => {
    if (!hasEditPermission || row.kind !== 'CUSTOM') return
    setEditing(row)
    setCreateOpen(true)
  }

  const openDelete = (row: LeaderboardDto) => {
    if (!hasEditPermission || row.kind !== 'CUSTOM') return
    setDeleteTarget(row)
    setDeleteOpen(true)
  }

  const openStandings = (row: LeaderboardDto) => {
    setStandingsBoardId(row.id)
    setStandingsOpen(true)
  }

  const openRebuild = (row: LeaderboardDto) => {
    if (!hasEditPermission) return
    setRebuildTarget(row)
    setRebuildOpen(true)
  }

  const handleSubmit = async (values: LeaderboardUpsertDto, id?: string) => {
    if (!editionId) return
    try {
      setLoading(true)
      if (id) {
        await updateLeaderboard(id, values)
        notify('Leaderboard updated successfully')
      } else {
        await createLeaderboard(editionId, values)
        notify('Leaderboard created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteLeaderboard(id)
      notify('Leaderboard deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (row: LeaderboardDto) => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      await setLeaderboardActive(row.id, !row.active)
      notify(`Leaderboard ${row.active ? 'deactivated' : 'activated'} successfully`)
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Commits an edited ordinal. The ordinal is stored exactly as typed — the backend never
   * renumbers the other boards — so duplicates are allowed and simply tie-break by creation
   * order. A blur that did not change the value (or left an unparseable one) just reverts.
   */
  const commitOrdinal = async (row: LeaderboardDto) => {
    const draft = ordinalDrafts[row.id]
    setOrdinalDrafts((prev) => {
      const next = { ...prev }
      delete next[row.id]
      return next
    })
    if (draft === undefined) return

    const parsed = Number(draft)
    if (draft.trim() === '' || !Number.isInteger(parsed) || parsed < 0) return
    if (parsed === row.ordinal) return

    try {
      setLoading(true)
      await setLeaderboardOrdinal(row.id, parsed)
      notify('Leaderboard order updated successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async (row: LeaderboardDto) => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      const { data: count } = await regenerateLeaderboard(row.id)
      notify(`Regenerated standings for ${count} user${count === 1 ? '' : 's'}`)
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Order',
        accessor: 'ordinal',
        render: (row: LeaderboardDto) =>
          hasEditPermission ? (
            <Input
              type="number"
              min={0}
              className="w-20"
              value={ordinalDrafts[row.id] ?? String(row.ordinal)}
              onChange={(e) =>
                setOrdinalDrafts((prev) => ({
                  ...prev,
                  [row.id]: e.target.value,
                }))
              }
              onBlur={() => commitOrdinal(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
          ) : (
            <span className="text-monochrome-2">{row.ordinal}</span>
          ),
      },
      {
        title: 'Name',
        accessor: 'name',
        render: (row: LeaderboardDto) => (
          <span className="text-monochrome-1">
            {translationName(row.translations, fallbackLabel(row))}
          </span>
        ),
      },
      {
        title: 'Scope',
        accessor: 'scope',
        render: (row: LeaderboardDto) => (
          <span className="text-monochrome-2">
            {row.scope === 'PARTNER'
              ? `Partner · ${partnerName(row.partnerId)}`
              : 'Festival'}
          </span>
        ),
      },
      {
        title: 'Kind',
        accessor: 'kind',
        render: (row: LeaderboardDto) => (
          <span className="text-monochrome-2">{row.kind}</span>
        ),
      },
      {
        title: 'Period / Window',
        accessor: 'periodType',
        render: (row: LeaderboardDto) => {
          if (row.periodType) {
            return <span className="text-monochrome-2">{row.periodType}</span>
          }
          if (row.startsAt && row.endsAt) {
            return (
              <span className="text-monochrome-2">
                {format(new Date(row.startsAt), 'MMM dd, yyyy • hh:mm a')}
                {' – '}
                {format(new Date(row.endsAt), 'MMM dd, yyyy • hh:mm a')}
              </span>
            )
          }
          return <span className="text-monochrome-2">—</span>
        },
      },
      {
        title: 'Active',
        accessor: 'active',
        render: (row: LeaderboardDto) => (
          <span className="text-monochrome-2">{row.active ? 'Yes' : 'No'}</span>
        ),
      },
      {
        title: 'Standings',
        accessor: '',
        render: (row: LeaderboardDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openStandings(row)
            }}
          >
            View
          </a>
        ),
      },
      {
        title: 'Regenerate',
        accessor: '',
        render: (row: LeaderboardDto) => {
          if (!hasEditPermission) {
            return <span className="text-monochrome-2">—</span>
          }
          // CUSTOM boards: routine one-click recompute (e.g. after editing the window).
          if (row.kind === 'CUSTOM') {
            return (
              <a
                className="view text-accent-1 underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  handleRegenerate(row)
                }}
              >
                Regenerate
              </a>
            )
          }
          // DEFAULT boards: a maintenance action (Redis data-loss recovery / backfill) —
          // visually distinct and gated behind a confirmation step.
          return (
            <a
              className="view text-accent-3 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openRebuild(row)
              }}
            >
              Rebuild
            </a>
          )
        },
      },
      {
        title: 'Active toggle',
        accessor: '',
        render: (row: LeaderboardDto) =>
          hasEditPermission ? (
            <a
              className="view text-accent-1 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                handleToggleActive(row)
              }}
            >
              {row.active ? 'Deactivate' : 'Activate'}
            </a>
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: LeaderboardDto) =>
          hasEditPermission && row.kind === 'CUSTOM' ? (
            <a
              className="view text-accent-1 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openEdit(row)
              }}
            >
              Edit
            </a>
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Delete',
        accessor: '',
        render: (row: LeaderboardDto) =>
          hasEditPermission && row.kind === 'CUSTOM' ? (
            <a
              className="view text-accent-3 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openDelete(row)
              }}
            >
              Delete
            </a>
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasEditPermission, partnerName, ordinalDrafts],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Create custom leaderboard
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="events-star" title="Leaderboards" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={boards}
          noDataMessage="No leaderboards yet"
        />
      </Card>

      <CreateLeaderboardModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        board={editing}
        partners={partners}
        onSubmit={handleSubmit}
      />

      <StandingsModal
        open={standingsOpen}
        onOpenChange={setStandingsOpen}
        boardId={standingsBoardId}
      />

      <RebuildConfirmationModal
        open={rebuildOpen}
        onOpenChange={setRebuildOpen}
        onConfirm={() => {
          if (rebuildTarget) handleRegenerate(rebuildTarget)
        }}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem={
          deleteTarget
            ? translationName(deleteTarget.translations, fallbackLabel(deleteTarget))
            : undefined
        }
        deleteFunc={() => {
          if (deleteTarget) handleDelete(deleteTarget.id)
        }}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
